import { forwardRef, useMemo } from 'react'
import { InputProps, Select as BaseSelect } from '@components/Inputs'
import { useBusinessContext } from '@context'
import { ICountry } from '@root/query/constants'
import { useCountries } from '@root/query/hooks'

interface CountrySelectProps extends InputProps {
  countries?: ICountry[]
}

const sortCountries = (countries: ICountry[]) => {
  return [...countries].sort((a, b) => a.name.localeCompare(b.name))
}

export const CountrySelect = forwardRef<any, CountrySelectProps>(
  ({ countries: providedCountries, ...rest }, ref) => {
    const hasCustomCountries = typeof providedCountries !== 'undefined'
    const { data: defaultCountries = [] } = useCountries({
      enabled: !hasCustomCountries
    })
    const { data: business } = useBusinessContext()

    const countries = hasCustomCountries ? providedCountries : defaultCountries
    const { businessCountry, sortedCountries } = useMemo(() => {
      if (!countries?.length) {
        return { businessCountry: undefined, sortedCountries: [] }
      }

      const sorted = sortCountries(countries)
      const businessCountryCode = business?.country?.toUpperCase()
      const matchingCountry = businessCountryCode
        ? sorted.find(country => country.code.toUpperCase() === businessCountryCode)
        : undefined

      return {
        businessCountry: matchingCountry,
        sortedCountries: sorted
      }
    }, [countries, business?.country])

    const showShortcuts = sortedCountries.length > 10

    const hasValue = Boolean(rest.value ?? rest.defaultValue)

    return (
      <BaseSelect {...rest} ref={ref}>
        {!hasValue && (
          <option key="empty" value="" disabled>
            {rest.placeholder || ''}
          </option>
        )}
        {showShortcuts && (
          <>
            {businessCountry && (
              <option key={`business-country-${businessCountry.code}`} value={businessCountry.code}>
                {businessCountry.name}
              </option>
            )}
            {sortedCountries.length > 0 && (
              <option key="divider" value="__divider" disabled>
                ---
              </option>
            )}
          </>
        )}
        {sortedCountries.map(country => (
          <option key={`country-${country.code}`} value={country.code}>
            {country.name}
          </option>
        ))}
      </BaseSelect>
    )
  }
)

CountrySelect.displayName = 'CountrySelect'
